import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, of, retry, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionStorageService } from './storage.service';
import { IAccount, UserService } from './user.service';

export interface IModelMetaData {
	createdAt?: number;
	createdBy?: string;
	updatedAt?: number;
	updatedBy?: string;
	version?: number;
}

export interface IModelBase extends IModelMetaData {
	id?: string;
}

type IPermissionList = string[];

export interface IUserPermissions {
	result: IUserPermission[];
}

export interface IUserPermission extends IModelBase {
	grantType: string;
	permissions: IPermissionList;
	permissionCodes: IPermissionList;
	email: string;
	status: string;
	name: string;
}

export interface IUserPolicies {
	result: IUserPolicy[];
}

export interface IUserPolicy extends IModelBase {
	type: string;
	name: string;
	description: string;
	permissions: string[];
	permissionCode: string[];
}

export interface IInviteUserResponse {
	result: IInviteUserResource;
}

export interface IInviteUserResource extends IModelBase {
	userId: string;
	type: string;
}

export interface IInviteUser {
	email: string;
	name: string;
	policyId: string;
}

@Injectable({
	providedIn: 'root',
})
export class UserPermissionsService {
	private userPermissionsCache = new Map<string, Observable<IUserPermission[]>>();

	private userPermissionsSubject$: BehaviorSubject<IUserPermission[]>;
	public userPermissions$: Observable<IUserPermission[]>;

	private userPermissionSubject$: BehaviorSubject<IUserPermission | null>;
	public userPermission$: Observable<IUserPermission | null>;

	// BehaviorSubject-backed (like userPermissions$/userPermission$ above) rather than a plain
	// field, so consumers - e.g. HasPermissionDirective - can react when the current session's
	// permission codes change, instead of only reading them once at construction time.
	private permissionsSubject$: BehaviorSubject<string[]>;
	public permissions$: Observable<string[]>;
	#permissionsMap: Set<string>;

	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {
		this.userPermissionsSubject$ = new BehaviorSubject<IUserPermission[]>([]);
		this.userPermissions$ = this.userPermissionsSubject$.asObservable();

		this.userPermissionSubject$ = new BehaviorSubject<IUserPermission | null>(null);
		this.userPermission$ = this.userPermissionSubject$.asObservable();

		this.permissionsSubject$ = new BehaviorSubject<string[]>([]);
		this.permissions$ = this.permissionsSubject$.asObservable();
		this.#permissionsMap = new Set();
		this.watchAuthIdentityForCacheReset();
	}

	public set permissions(value: string[]) {
		this.#permissionsMap.clear();
		this.#permissionsMap = new Set(value);
		this.permissionsSubject$.next(value);
	}

	public get permissions(): string[] {
		return this.permissionsSubject$.value;
	}

	public hasPermissions(permissions: string[]): boolean {
		if (permissions.length === 0) {
			return false;
		}
		return permissions.every((permission) => this.#permissionsMap.has(permission));
	}

	public get userPermission(): IUserPermission | null {
		return this.userPermissionSubject$.value || null;
	}

	public get userPermissions(): IUserPermission[] | null {
		return this.userPermissionsSubject$.value || null;
	}

	public fetchUserPermissions(acc: IAccount = null): void {
		const account = acc || this.userService.account;
		const cacheKey = this.getUserPermissionsCacheKey(account);
		if (this.userPermissionsCache.has(cacheKey)) {
			const cached$ = this.userPermissionsCache.get(cacheKey);
			cached$.pipe(filter(() => this.userPermissionsCache.get(cacheKey) === cached$)).subscribe((users) => this.userPermissionsSubject$.next(users));
			return;
		}

		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};

		const fetchNextBatch = (offset = 0, users: IUserPermission[] = []): Observable<IUserPermission[]> => {
			const offsetQuery = offset > 0 ? `&offset=${offset}` : '';
			return this.http.get<IUserPermissions>(`${environment.awsEnvUri}/iam/organizations/${account.organization.id}/accounts/${account.id}/users?permissions=insights:UserReportGet${offsetQuery}`, { headers }).pipe(
				retry(1),
				switchMap((response) => {
					const nextUsers = [...users, ...response.result];
					if (response.result?.length < 20) {
						return of(nextUsers);
					}

					return fetchNextBatch(nextUsers.length, nextUsers);
				}),
			);
		};

		const users$ = fetchNextBatch().pipe(
			catchError((error) => {
				this.userPermissionsCache.delete(cacheKey);
				return this.handleError(error);
			}),
			shareReplay(1),
		);

		this.userPermissionsCache.set(cacheKey, users$);
		users$.pipe(filter(() => this.userPermissionsCache.get(cacheKey) === users$)).subscribe((users) => this.userPermissionsSubject$.next(users));
	}

	public clearUserPermissionsCache(acc: IAccount = null): void {
		if (acc) {
			this.deleteUserPermissionsCacheForAccount(acc);
			return;
		}

		this.userPermissionsCache.clear();
	}

	public fetchUserPolices(acc: IAccount = null): Observable<IUserPolicies> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		return this.http
			.get<IUserPolicies>(`${environment.awsEnvUri}/iam/organizations/${acc ? acc.organization.id : account.organization.id}/accounts/${acc ? acc.id : account.id}/policies/managed`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public inviteUserWithPermissions(inviteUser: IInviteUser): Observable<IInviteUserResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
		};
		console.log(`invite user: ${JSON.stringify(inviteUser)}`);
		return this.http.post<IInviteUserResponse>(`${environment.awsEnvUri}/iam/organizations/${account.organization.id}/accounts/${account.id}/invite`, inviteUser, { headers }).pipe(
			retry(1),
			tap(() => {
				this.clearUserPermissionsCache(account);
				this.userService.clearUsersCache(account);
			}),
			catchError(this.handleError),
		);
	}

	private getUserPermissionsCacheKey(account: IAccount): string {
		return `${this.getCurrentUserCacheKey()}:${this.getAccountCacheKey(account)}:insights:UserReportGet`;
	}

	private getAccountCacheKey(account: IAccount): string {
		return `${account.organization.id}:${account.id}`;
	}

	private deleteUserPermissionsCacheForAccount(account: IAccount): void {
		const accountCacheKey = this.getAccountCacheKey(account);
		for (const cacheKey of this.userPermissionsCache.keys()) {
			if (cacheKey.includes(`:${accountCacheKey}:`)) {
				this.userPermissionsCache.delete(cacheKey);
			}
		}
	}

	private getCurrentUserCacheKey(): string {
		return this.sessionStorageService.get('User.IAM.Id') || this.sessionStorageService.get('User.Email') || this.sessionStorageService.get('User.Authorization') || '';
	}

	private watchAuthIdentityForCacheReset(): void {
		this.sessionStorageService.watch('User.IAM.Id').subscribe(() => {
			this.clearUserPermissionsCache();
			this.userPermissionsSubject$.next([]);
		});
		this.sessionStorageService.watch('User.Authorization').subscribe(() => {
			this.clearUserPermissionsCache();
			this.userPermissionsSubject$.next([]);
		});
		this.userService.accountChanged$.subscribe(() => {
			this.clearUserPermissionsCache();
			this.userPermissionsSubject$.next([]);
		});
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			console.log(errorMessage);
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}
}
